import { z } from 'zod'
import { idSchema, reasonSchema, revisionSchema } from '../validators'
import { ENGAGEMENT_STATUSES, FEED_DATASETS, FEED_PROVIDERS, LICENCE_STATUSES } from '../vocab'

/**
 * Admin: engagement subscriptions, per-user restrictions and dataset
 * entitlements (P-ID-004, P-ADM-010, P-CAN-005, R-07). Every change needs a
 * reason of at least 8 characters, takes effect on the next request, keeps an
 * append-only history and is audited on the platform chain and the org chain.
 */

/** Without `org_id` the first investor org (by name) is shown. */
export const adminOrgQuerySchema = z.object({ org_id: idSchema.optional() })

export const adminOrgRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  status: z.string(),
})
export type AdminOrgRef = z.infer<typeof adminOrgRefSchema>

export const grantRecordSchema = z.object({
  id: z.string(),
  granted_at: z.string(),
  granted_by: z.string(),
  granted_by_name: z.string().nullable(),
  reason: z.string(),
  revoked_at: z.string().nullable(),
  revoked_by: z.string().nullable(),
  revoked_by_name: z.string().nullable(),
  revoke_reason: z.string().nullable(),
  revision: z.number().int(),
})
export type GrantRecord = z.infer<typeof grantRecordSchema>

export const subscriptionEngagementSchema = z.object({
  engagement_id: z.string(),
  title: z.string(),
  issuer_id: z.string(),
  issuer_name: z.string(),
  theme: z.string().nullable(),
  status: z.enum(ENGAGEMENT_STATUSES.values),
  /** Set when the case is client-specific (D-03): only that org may subscribe. */
  client_org_id: z.string().nullable(),
  /** False when the case belongs to another client. */
  subscribable: z.boolean(),
  subscription: grantRecordSchema.nullable(),
  history: z.array(grantRecordSchema),
})
export type SubscriptionEngagement = z.infer<typeof subscriptionEngagementSchema>

export const restrictionSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user_name: z.string(),
  user_email: z.string(),
  engagement_id: z.string(),
  engagement_title: z.string(),
  reason: z.string(),
  created_by: z.string(),
  created_at: z.string(),
})
export type Restriction = z.infer<typeof restrictionSchema>

export const orgMemberSchema = z.object({
  user_id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
})

export const subscriptionOverviewSchema = z.object({
  org: adminOrgRefSchema,
  orgs: z.array(adminOrgRefSchema),
  engagements: z.array(subscriptionEngagementSchema),
  restrictions: z.array(restrictionSchema),
  members: z.array(orgMemberSchema),
})
export type SubscriptionOverview = z.infer<typeof subscriptionOverviewSchema>

export const subscriptionGrantSchema = z.strictObject({
  org_id: idSchema,
  engagement_id: idSchema,
  reason: reasonSchema,
})
export type SubscriptionGrant = z.infer<typeof subscriptionGrantSchema>

export const grantRevokeSchema = z.strictObject({
  revision: revisionSchema,
  reason: reasonSchema,
})
export type GrantRevoke = z.infer<typeof grantRevokeSchema>

export const restrictionCreateSchema = z.strictObject({
  org_id: idSchema,
  user_id: idSchema,
  engagement_id: idSchema,
  reason: reasonSchema,
})
export type RestrictionCreate = z.infer<typeof restrictionCreateSchema>

export const restrictionLiftSchema = z.strictObject({ reason: reasonSchema })
export type RestrictionLift = z.infer<typeof restrictionLiftSchema>

export const entitlementDatasetSchema = z.object({
  dataset: z.enum(FEED_DATASETS.values),
  label: z.string(),
  provider_id: z.enum(FEED_PROVIDERS.values),
  provider_name: z.string(),
  licence_status: z.enum(LICENCE_STATUSES.values),
  simulated: z.boolean(),
  entitlement: grantRecordSchema.nullable(),
  history: z.array(grantRecordSchema),
})
export type EntitlementDataset = z.infer<typeof entitlementDatasetSchema>

export const entitlementOverviewSchema = z.object({
  org: adminOrgRefSchema,
  orgs: z.array(adminOrgRefSchema),
  datasets: z.array(entitlementDatasetSchema),
})
export type EntitlementOverview = z.infer<typeof entitlementOverviewSchema>

export const entitlementGrantSchema = z.strictObject({
  org_id: idSchema,
  dataset: z.enum(FEED_DATASETS.values),
  reason: reasonSchema,
})
export type EntitlementGrant = z.infer<typeof entitlementGrantSchema>

/** GET /admin/overview: platform health for the admin landing page. */
export const adminOverviewSchema = z.object({
  generated_at: z.string(),
  orgs: z.object({ total: z.number().int(), investor: z.number().int(), suspended: z.number() }),
  users: z.object({
    total: z.number().int(),
    active: z.number().int(),
    invited: z.number().int(),
    deactivated: z.number().int(),
    platform: z.number().int(),
    signed_in_7d: z.number().int(),
  }),
  /** Approximate: counted from the session store. Null when it could not be read. */
  sessions_active: z.number().int().nullable(),
  subscriptions_active: z.number().int(),
  entitlements_active: z.number().int(),
  jobs: z.array(
    z.object({
      job: z.string(),
      cron: z.string(),
      status: z.string(),
      started_at: z.string(),
      finished_at: z.string().nullable(),
    }),
  ),
  audit: z.object({
    status: z.enum(['ok', 'failed', 'unknown']),
    last_verified_at: z.string().nullable(),
    chains: z.number().int().nullable(),
    open_failures: z.number().int(),
  }),
  alerts: z.object({
    open: z.number().int(),
    critical: z.number().int(),
    warning: z.number().int(),
    info: z.number().int(),
  }),
  signals: z.object({ new: z.number().int(), mapping_review: z.number().int() }),
})
export type AdminOverview = z.infer<typeof adminOverviewSchema>
